import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import './project1.css';
import Button from 'react-bootstrap/Button';
import 'bootstrap/dist/css/bootstrap.min.css';
import DP_Copy1 from './images/DP.png';
import App from './App';

function Project1() {
  const [main, setMain] = useState(false);
  const gotoMainPage = () => {
    setMain(true);
  };

  return (
    <>
      {!main ? (
        <div className="body">
          <div className  = "headerP1">
            <h2 id = "Title">Demand Paging Simulator</h2>
            <div id = "P1Github">
              <a href="https://github.com/AkhilTheBoss/Demand-Paging" target="_blank" rel="noopener noreferrer">
                <Button variant="secondary">Github</Button>
              </a>
            </div>
            <div id = "mainPage">
              <Button variant="primary" onClick={gotoMainPage}>Main Page</Button>
            </div>
          </div>
          <br />
          <h4 className = "Heading">Goal</h4>
          <p className = "Content">
            The goal is to simulate and analyze the behavior of the demand paging system in an operating system.
          </p>
          <h4 className = "Heading">Introduction</h4>
          <p className = "Content">
            Demand paging allows operating system to load or unload pages into memory on a demand basis rather than loading the entire data into memory once.
            The significance of demand paging lies in its ability to optimize memory utilization and improve system performance.
            Large memory is divided into smaller pre-defined sized chunks called pages. The purpose of dividing memory into pages is to facilitate efficient memory management.
          </p>
          <h4 className = "Heading">Diagram</h4>
          <p className = "Content">
            <img src={DP_Copy1} alt="DP Copy1" className = "Diagram"/>
          </p>
          <h4 className = "Heading">Commonly Used Terminologies</h4>
          <p className = "Content">
            <ul>
              <li><strong>Page:</strong> The virtual memory is divided into fixed-size blocks of memory called pages. Each page has a fixed size called page size, which is determined by the operating system. The virtual page number of the virtual address corresponds to a specific page in the virtual memory.</li>
              <li><strong>Frame:</strong> The main memory id divided into fixed-size blocks of memory called frames. The size of each frame is the same as that of a page. The physical page number of the physical address corresponds to a specific page in the main memory.</li>
              <li><strong>Translation Lookaside Buffer (TLB):</strong> It is a type of cache that stores recent virtual to physical address translations to enable faster retrieval of data.</li>
              <li><strong>Page Table:</strong> It is a table that stores the virtual to physical address mapping.</li>
              <li><strong>Secondary Storage:</strong> It is used as an extension of RAM to store pages that are not currently in use. Secondary storage has a larger capacity than RAM, due to which it can accommodate large number of pages compared to the RAM. This provides us with an illusion of 'infinite memory'.</li>
              <li><strong>Valid Bit:</strong> Valid bit is set (Valid bit = 1) for an entry in the Page Table if the respective page is present in the RAM else Valid bit = 0.</li>
              <li><strong>Dirty Bit:</strong> Dirty bit = 1 when the page has been modified but not saved in the secondary storage yet else dirty bit = 0. When a block of memory is to be replaced from the RAM to the secondary storage, its corresponding dirty bit in the Page Table is checked to see if the block needs to be written back to secondary storage
                  before being replaced.</li>
              <li><strong>Page Fault:</strong> An event that occurs when a page of memory is tried to be accessed that isn't currently available in the main memory.</li>
              <li><strong>Page Fault Handler:</strong> It is responsible for handling page faults by storing(or replacing) a page from secondary storage to main memory. The Page table and TLB are updated with the physical address.</li>
            </ul>
          </p>
          <h4 className = "Heading">Advantages of demand paging</h4>
          <p className = "Content">
            <ul>
              <li>Allows for efficient utilization of memory by loading only the required pages (required chunk of data) into physical memory. This approach reduces memory wastage.</li>
              <li>We can run programs that are larger than physical memory.</li>
              <li>Low latency: very fast response time to requests</li>
            </ul>
          </p>
          <h4 className = "Heading">Disadvantages of demand paging</h4>
          <p className = "Content">
            <ul>
              <li>Introduces the possibility of page faults, which occur when a required page is not in physical memory and must be fetched from secondary storage. This slows down the program execution.</li>
              <li>Increases complexity to the memory management system as it requires additional hardware support and algorithms</li>
            </ul>
          </p>
          <h4 className = "Heading">Components of demand paging</h4>
          <p className = "Content">
            <ul>
              <li>Translation Lookaside Buffer (TLB)</li>
              <li>Page Table</li>
              <li>Page Fault Handler</li>
              <li>Secondary Storage</li>
            </ul>
          </p>
          <h4 className = "Heading">Virtual address</h4>
          <p className = "Content">
            There is a list of 1000 virtual addresses in VirtualAddress.txt. Each virtual address will extract a certain byte from memory. I created a page table of 256 entries hence the virtual page number (VPN) is 8 bits (2^VPN = 256). Hence, the offset is 8 bits (16 - VPN), meaning the number of bytes per page is 256.
            The virtual page number is used as an index for the Page Table to receive the Physical Address translation, it is also used to retrieve the appropriate page from secondary storage. The offset is used to extract the respective byte from the selected frame in main memory.
          </p>
          <h4 className = "Heading">Size of the hardware components</h4>
          <p className = "Content">
            <ul>
              <li>Number of Page Table Entries: 256</li>
              <li>Number of TLB Entries: 16</li>
              <li>Page Size: 256 bytes</li>
              <li>Frame Size: 256 bytes</li>
              <li>Number of Frames: 256</li>
              <li>RAM Size: 65536 bytes</li>
              <li>Secondary Storage Size: 65536 bytes</li>
            </ul>
          </p>
          <h4 className = "Heading">Translation Lookaside Buffer (TLB)</h4>
          <p className = "Content">
            It is a type of cache that stores recent virtual to physical address translations to enable faster retrieval of data.
            <p>{'\n'}</p>
            I created a 16 entry TLB, which checks if a specific virtual address has its translated physical address. If there is a virtual address entry in the TLB, it is a TLB hit and the translated physical address is extracted, accessing the corresponding frame in the RAM.
            If there is no entry for the specific virtual address in the TLB, it is a TLB miss and the Page Table is accessed. If it is a TLB miss, the TLB will later be updated with the virtual address and the translated physical address. If the TLB is full, it will undergo a
            replacement algorithm (FIFO - First In First Out) to replace a TLB entry.
          </p>
          <h4 className = "Heading">Page Table</h4>
          <p className = "Content">
            It is a table that stores the virtual to physical address mapping.
            <p>{'\n'}</p>
            I created a 256 entry Page Table. If it is a TLB miss, the Page Table is checked. The Page Table contains all the virtual to physical address mappings. If there is a translated physical address present for the virtual address, it is a Page Table hit and the specific byte is accessed from the RAM with the physical address.
            If there isn't a translation for the virtual address, it is a Page Table miss and the page fault handler is called to access the secondary storage.
          </p>
          <h4 className = "Heading">Page Fault Handler</h4>
          <p className = "Content">
            If it is a TLB miss or Page Table Miss, it is called as a page fault as the respective page is not present in the RAM. In this case, the page fault handler is called where the respective page is stored in a RAM frame. The TLB and Page Table are then updated with the physical address of the RAM frame.
          </p>
          <h4 className = "Heading">Secondary Storage</h4>
          <p className = "Content">
            BACKING_STORE.bin, a 65536 bytes (256 pages) file, is my secondary storage. If there is a Page Table miss, the secondary storage is accessed with the virtual page number.
          </p>
          <h4 className = "Heading">Results</h4>
          <p className = "Content">
            In my project, I conducted performance analysis of a demand paging system. The results, recorded in the "results.txt" file, indicate that the TLB (Translation Lookaside Buffer) hit rate was 5.5% and the Page Fault rate was 24.4%. This implies that the Page Table Hit rate, which represents successful lookups in the page table, was 70.1%.
            Furthermore, the analysis revealed that accessing data from the TLB incurred a latency of 47 microseconds, while accessing data from the Page Table resulted in a latency of 493 microseconds. On the other hand, retrieving data from the secondary storage exhibited a significantly higher latency of 966 microseconds.
            These findings highlight the significant performance advantage of the TLB, which enables faster data access, as compared to the slower secondary storage.
          </p>
          <h4 className = "Heading">Scope For Improvement</h4>
          <p className = "Content">
            <ul>
              <li>Write Operations: Can increase the scope of my project to handle write operations effectively.</li>
              <li>Dirty Page Management: Can improve the project functionality to handle dirty pages, which are modified but not yet saved in secondary storage.
                  Improvise my code to track dirty pages, specify when to write them back to secondary storage, ensure data consistency.</li>
              <li>Advanced Replacement Algorithms: Use an algorithm like LRU (Least Recently Used) instead of FIFO (First In First Out) for the TLB. This can increase the TLB Hit rate, improving its performance.</li>
              <li>Multi-level Page Table: Implementing multi-level page tables will add several benefits like reduced memory overhead, faster Page Table lookup, leading to faster system performance</li>
            </ul>
          </p>
        </div>
      ):(
        <App />
      )}
    </>
  );
}

export default Project1;
